import React, { useEffect, useRef, useState } from 'react';
import { FiEdit2, FiX, FiMinus } from 'react-icons/fi';

const INBOX_DRAG_TYPE = 'application/x-mindmappi-inbox-idea';

export { INBOX_DRAG_TYPE };

const DRAFT_PREFIX = 'mindmappi:inbox-draft:';

function readDraft(scope) {
  try {
    const value = JSON.parse(sessionStorage.getItem(DRAFT_PREFIX + scope));
    if (value && typeof value.title === 'string' && typeof value.notes === 'string') {
      return { title: value.title, notes: value.notes, target: value.target?.id ? value.target : null };
    }
  } catch { /* Storage may be unavailable. Keep capture usable. */ }
  return { title: '', notes: '', target: null };
}

export function suggestIdeaTitle(notes) {
  const first = notes.trim().split(/[\n.!?]/u)[0].trim() || notes.trim();
  return Array.from(first).slice(0, 80).join('');
}

const stopCanvasEvent = (event) => event.stopPropagation();

function InboxIcon({ size = 18 }) {
  return (
    <svg
      aria-hidden="true"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 5h16v14H4z" />
      <path d="M4 14h4l2 2h4l2-2h4" />
    </svg>
  );
}

function InboxCard({
  node,
  branchCount,
  isMobile,
  canWrite,
  canEdit = canWrite,
  onDelete,
  onDragStart,
  onDragEnd,
  destinations = [],
  onPlace,
  onEdit,
  draftScope,
}) {
  const [destination, setDestination] = useState('');
  const draftKey = `${DRAFT_PREFIX}${draftScope}:edit:${node.id}`;
  const [edit, setEdit] = useState(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(draftKey));
      return saved && typeof saved.text === 'string' && typeof saved.info === 'string' && saved.original ? saved : null;
    } catch { return null; }
  });
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const [storageFailed, setStorageFailed] = useState(false);
  const pendingRef = useRef(false);
  const editButton = useRef(null);
  const titleInput = useRef(null);
  const canDragToMap = canWrite && !isMobile && !edit && !pending;
  useEffect(() => {
    try {
      if (edit) sessionStorage.setItem(draftKey, JSON.stringify(edit));
      else sessionStorage.removeItem(draftKey);
      setStorageFailed(false);
    } catch { setStorageFailed(true); }
  }, [draftKey, edit]);
  const editing = Boolean(edit);
  useEffect(() => { if (editing) titleInput.current?.focus(); }, [editing]);
  const startEdit = () => {
    setEdit({ text: node.text || '', info: node.info || '', original: { text: node.text || '', info: node.info || '' } });
    setError('');
  };
  const finishEdit = () => {
    setEdit(null); setError('');
    window.requestAnimationFrame(() => editButton.current?.focus());
  };
  const saveEdit = async () => {
    if (pendingRef.current || !canEdit || !edit?.text.trim()) return;
    pendingRef.current = true; setPending(true); setError('');
    try {
      const result = await onEdit(node.id, edit);
      if (result === false) throw new Error('Could not save. Your edits are still here.');
      finishEdit();
    } catch (failure) { setError(failure.message || 'Could not save. Your edits are still here.'); }
    finally { pendingRef.current = false; setPending(false); }
  };
  const place = async () => {
    if (pendingRef.current || !destination || !canWrite) return;
    pendingRef.current = true; setPending(true); setError('');
    try {
      if (!destinations.some(item => item.id === destination)) throw new Error('Choose an available destination.');
      if (await onPlace(node.id, destination) === false) throw new Error('Could not place this idea. Please try again.');
    } catch (failure) { setError(failure.message || 'Could not place this idea. Please try again.'); }
    finally { pendingRef.current = false; setPending(false); }
  };
  const cardStatus = branchCount > 0
    ? `${branchCount} ${branchCount === 1 ? 'child' : 'children'}`
    : node.creatorName || 'Captured idea';

  return (
    <article
      className="idea-inbox__card"
      aria-label={`Inbox idea: ${node.text || 'Untitled'}`}
      draggable={canDragToMap}
      onDragStart={(event) => {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData(INBOX_DRAG_TYPE, node.id);
        event.dataTransfer.setData('text/plain', `mindmappi-inbox:${node.id}`);
        onDragStart?.(node.id);
      }}
      onDragEnd={() => onDragEnd?.()}
      data-inbox-idea-id={node.id}
      title={canDragToMap ? 'Drag onto an idea in the map' : undefined}
    >
      {error && <p role="alert" className="idea-inbox__status">{error}</p>}
      {edit ? <div className="idea-inbox__edit" onKeyDown={event => {
        event.stopPropagation();
        if ((event.ctrlKey || event.metaKey) && event.key === 'Enter' && !event.nativeEvent?.isComposing) { event.preventDefault(); saveEdit(); }
      }}>
        <textarea ref={titleInput} aria-label="Edit idea title" placeholder="Title" value={edit.text} rows={2} disabled={pending || !canEdit}
          onChange={event => setEdit({ ...edit, text: event.target.value })} />
        <textarea aria-label="Edit idea notes" placeholder="Add a description or notes" value={edit.info} rows={4} maxLength={20000} disabled={pending || !canEdit}
          onChange={event => setEdit({ ...edit, info: event.target.value })} />
        {storageFailed && <p role="status">Edits could not be backed up. Keep this panel open until saved.</p>}
        {(node.text !== edit.original.text || (node.info || '') !== edit.original.info) && <div className="idea-inbox__status">
          <p>This idea changed elsewhere. Latest title: {node.text}</p>
          <details><summary>Latest notes</summary><p>{node.info || 'No notes'}</p></details>
          <button type="button" disabled={pending} onClick={startEdit}>Discard edits and use latest</button>
        </div>}
        <div className="idea-inbox__edit-actions">
          <button type="button" disabled={pending || !canEdit || !edit.text.trim()} onClick={saveEdit}>{pending ? 'Saving…' : 'Save changes'}</button>
          <button type="button" className="is-secondary" disabled={pending} onClick={finishEdit}>Cancel</button>
        </div>
      </div> : <div className="idea-inbox__card-text">{node.text}</div>}
      {!edit && node.info && <details className="idea-inbox__notes" onPointerDown={stopCanvasEvent}>
        <summary>Notes</summary><p>{node.info}</p>
      </details>}
      {!edit && isMobile && canWrite && onPlace && <div className="idea-inbox__placement">
        <select aria-label={`Destination for ${node.text}`} value={destination} disabled={pending} onChange={event => setDestination(event.target.value)}>
          <option value="">Choose destination…</option>
          {destinations.map(item => <option key={item.id} value={item.id}>{item.text || 'Untitled idea'}</option>)}
        </select>
        <button type="button" disabled={pending || !destinations.some(item => item.id === destination)} onClick={place}>Place in map</button>
      </div>}
      {!!node.tags?.length && (
        <div className="idea-inbox__tags">
          {node.tags.map((tag) => <span key={tag}>#{tag}</span>)}
        </div>
      )}
      <footer>
        <span>{canDragToMap && <svg role="img" aria-label="Draggable idea" width="14" height="14" viewBox="0 0 14 14" fill="currentColor" style={{ verticalAlign: 'middle', marginRight: 6 }}>
          {[3, 7, 11].flatMap(y => [4, 9].map(x => <circle key={`${x}-${y}`} cx={x} cy={y} r="1.2" />))}
        </svg>}{cardStatus}</span>
        {canWrite && !edit && (
          <div>
            {canEdit && onEdit && <button ref={editButton} type="button" className="idea-inbox__icon-button" onClick={startEdit} aria-label="Edit inbox idea" title="Edit idea" disabled={pending}><FiEdit2 aria-hidden="true" size={15} /></button>}
            <button type="button" className="idea-inbox__icon-button" disabled={pending} onClick={() => onDelete(node.id)} aria-label="Delete inbox idea" title="Delete idea">
              <FiX aria-hidden="true" size={17} />
            </button>
          </div>
        )}
      </footer>
    </article>
  );
}

export default function IdeaInbox({
  draftScope = 'guest',
  currentUserId = null,
  destinations,
  onPlaceIdea,
  mode,
  isMobile,
  mobileView,
  isDarkMode,
  inbox,
  captureTarget,
  canWrite,
  isDropActive,
  onModeChange,
  onMobileViewChange,
  onAddIdea,
  onDeleteIdea,
  onEditIdea,
  onReturnFocus,
  onInboxDragStart,
  onInboxDragEnd,
}) {
  const [initial] = useState(() => readDraft(draftScope));
  const [draft, setDraft] = useState(initial.title);
  const [info, setInfo] = useState(initial.notes);
  const [draftTarget, setDraftTarget] = useState(initial.target);
  const [restored, setRestored] = useState(Boolean(initial.title || initial.notes));
  const [storageFailed, setStorageFailed] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const composerRef = useRef(null);
  const panelRef = useRef(null);
  const [keyboardInset, setKeyboardInset] = useState(0);
  useEffect(() => {
    if (!isMobile || !window.visualViewport) return undefined;
    const viewport = window.visualViewport;
    let frame;
    const resize = () => {
      setKeyboardInset(Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop));
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        const focused = document.activeElement;
        if (panelRef.current?.contains(focused) && focused?.tagName === 'TEXTAREA') focused.scrollIntoView?.({ block: 'nearest' });
      });
    };
    resize();
    viewport.addEventListener('resize', resize);
    viewport.addEventListener('scroll', resize);
    return () => { window.cancelAnimationFrame(frame); viewport.removeEventListener('resize', resize); viewport.removeEventListener('scroll', resize); };
  }, [isMobile]);
  const hasDraft = Boolean(draft || info);
  const effectiveTarget = hasDraft ? draftTarget : captureTarget;
  const targetMissing = Boolean(effectiveTarget && destinations && !destinations.some(node => node.id === effectiveTarget.id));
  const suggestedTitle = !draft.trim() ? suggestIdeaTitle(info) : '';

  useEffect(() => {
    try {
      if (hasDraft) sessionStorage.setItem(DRAFT_PREFIX + draftScope, JSON.stringify({ title: draft, notes: info, target: draftTarget }));
      else sessionStorage.removeItem(DRAFT_PREFIX + draftScope);
      setStorageFailed(false);
    } catch { setStorageFailed(true); }
  }, [draftScope, draft, info, draftTarget, hasDraft]);

  const updateDraft = (value) => {
    if (!hasDraft && value) setDraftTarget(captureTarget || null);
    if (!value && !info) setDraftTarget(null);
    setDraft(value);
    setError('');
  };

  const discard = () => {
    setInfo(''); setDraft(''); setDraftTarget(null); setRestored(false); setError('');
  };

  const submit = async () => {
    if ((!draft.trim() && !info.trim()) || submittingRef.current || !canWrite || targetMissing) return;
    submittingRef.current = true;
    setSubmitting(true);
    setError('');
    try {
      const added = await onAddIdea(draft.trim() || suggestedTitle, effectiveTarget?.id || null, info);
      if (added === false) { setError('Could not add this idea. Your draft is still here.'); return; }
      discard();
      window.requestAnimationFrame(() => composerRef.current?.focus());
    } catch {
      setError('Could not add this idea. Your draft is still here. Try again.');
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const mobileTabs = isMobile && (
    <nav className="idea-inbox__mobile-tabs" data-export-ignore="true" data-viewport-occlusion="true">
      <button
        type="button"
        className={mobileView === 'ideas' ? 'is-active' : ''}
        onClick={() => onMobileViewChange('ideas')}
      >
        Ideas {inbox.count > 0 && <span>{inbox.count}</span>}
      </button>
      <button
        type="button"
        className={mobileView === 'map' ? 'is-active' : ''}
        onClick={() => onMobileViewChange('map')}
      >
        Map
      </button>
    </nav>
  );

  const expanded = (isMobile && mobileView === 'ideas') || mode === 'expanded';
  const showPanel = expanded && (!isMobile || mobileView === 'ideas');
  const showBubble = !showPanel && (
    mode === 'collapsed' || (isMobile && mobileView === 'map' && mode !== 'hidden')
  );

  return (
    <>
      {mobileTabs}
      {showBubble && (
        <button
          type="button"
          className={`idea-inbox__bubble ${isDropActive ? 'is-drop-active' : ''}`}
          data-export-ignore="true"
          data-viewport-occlusion="true"
          data-idea-inbox-drop-zone="true"
          onClick={() => {
            onModeChange('expanded');
            if (isMobile) onMobileViewChange('ideas');
          }}
          aria-label={`Open idea inbox with ${inbox.count} ${inbox.count === 1 ? 'idea' : 'ideas'}`}
        >
          <InboxIcon />
          <span>Inbox</span>
          <strong>{inbox.count}</strong>
        </button>
      )}
      {showPanel && (
        <aside
          ref={panelRef}
          style={isMobile ? { bottom: keyboardInset } : undefined}
          className={`idea-inbox__panel ${isMobile ? 'is-mobile' : ''} ${isDropActive ? 'is-drop-active' : ''}`}
          data-dark={isDarkMode ? 'true' : 'false'}
          data-export-ignore="true"
          data-viewport-occlusion="true"
          data-idea-inbox-drop-zone="true"
          onPointerDown={stopCanvasEvent}
          onMouseDown={stopCanvasEvent}
          onClick={stopCanvasEvent}
        >
          <header className="idea-inbox__header">
            <div>
              <InboxIcon size={20} />
              <span>
                <strong>{effectiveTarget ? `Ideas for ${effectiveTarget.text || 'Untitled'}` : 'Idea inbox'}</strong>
                {!effectiveTarget && <small>{`${inbox.count} unplaced ${inbox.count === 1 ? 'idea' : 'ideas'}`}</small>}
              </span>
            </div>
            <div>
              <button
                type="button"
                onClick={() => {
                  onModeChange('collapsed');
                  if (isMobile) onMobileViewChange('map');
                  onReturnFocus?.();
                }}
                title="Collapse inbox"
                aria-label="Collapse idea inbox"
              >
                <FiMinus aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={() => {
                  onModeChange('hidden');
                  if (isMobile) onMobileViewChange('map');
                  onReturnFocus?.();
                }}
                title="Hide inbox"
                aria-label="Hide idea inbox"
              >
                <FiX aria-hidden="true" />
              </button>
            </div>
          </header>

          <div className="idea-inbox__composer">
            {restored && <p role="status" className="idea-inbox__status">Draft restored in this tab.</p>}
            {storageFailed && hasDraft && <p role="status">Draft could not be backed up in this tab. Keep it open until added.</p>}
            {targetMissing && <p role="alert">The original destination is unavailable. <button type="button" onClick={() => setDraftTarget(null)}>Use inbox instead</button></p>}
            {error && <p role="alert">{error}</p>}
            <textarea
              id="inbox-idea-title"
              aria-label="Title"
              ref={composerRef}
              value={draft}
              disabled={!canWrite || submitting}
              onChange={(event) => updateDraft(event.target.value)}
              onKeyDown={(event) => {
                const isComposing = event.isComposing || event.nativeEvent?.isComposing;
                if (isMobile || event.key !== 'Enter' || event.shiftKey || isComposing || event.keyCode === 229) return;
                event.preventDefault();
                submit();
              }}
              placeholder={canWrite ? 'Title' : 'You have view-only access'}
              enterKeyHint={isMobile ? 'enter' : 'done'}
              rows={2}
            />
            <textarea
              id="inbox-idea-info"
              aria-label="Notes (optional)"
              value={info}
              disabled={!canWrite || submitting}
              maxLength={20000}
              onChange={(event) => {
                const value = event.target.value;
                if (!hasDraft && value) setDraftTarget(captureTarget || null);
                if (!draft && !value) setDraftTarget(null);
                setInfo(value);
              }}
              placeholder="Add a description or notes"
              rows={3}
            />
            {suggestedTitle && <p className="idea-inbox__status">Suggested title: {suggestedTitle} <button type="button" disabled={submitting} onClick={() => { setDraft(suggestedTitle); composerRef.current?.focus(); }}>Edit title</button></p>}
            <div>
              <span title={effectiveTarget?.text}>
                {effectiveTarget ? `Adding under ${effectiveTarget.text || 'selected idea'}` : 'Adding to the inbox'}
              </span>
              <button type="button" disabled={(!draft.trim() && !info.trim()) || submitting || !canWrite || targetMissing} onClick={submit}>
                {submitting ? 'Adding…' : 'Add idea'}
              </button>
            </div>
            {hasDraft && <div><span>{storageFailed ? 'Draft not backed up' : 'Draft kept in this tab'}</span><button type="button" disabled={submitting} onClick={discard}>Discard draft</button></div>}
          </div>

          <div className="idea-inbox__list">
            {inbox.rootNodes.map((node) => (
              <InboxCard
                key={node.id}
                node={node}
                branchCount={inbox.branchCountByRootId.get(node.id) || 0}
                isMobile={isMobile}
                canWrite={canWrite}
                canEdit={canWrite && !(node.userId && currentUserId && node.userId !== currentUserId)}
                destinations={destinations}
                onPlace={onPlaceIdea}
                onEdit={onEditIdea}
                draftScope={draftScope}
                onDelete={onDeleteIdea}
                onDragStart={onInboxDragStart}
                onDragEnd={onInboxDragEnd}
              />
            ))}
            {!inbox.rootNodes.length && (
              <div className="idea-inbox__empty">
                <InboxIcon size={24} />
                <strong>Your inbox is clear</strong>
                <span>Capture a thought without deciding where it belongs yet.</span>
              </div>
            )}
          </div>
        </aside>
      )}
    </>
  );
}
